import { useEffect, type RefObject } from 'react';
import { useMotionPolicy } from './Motion';

const targets = '.price-card, .sdk-resource, .branding-asset, .official-partners__feature, .docs-home__links > a';

export function usePublicReveals(ref: RefObject<HTMLElement | null>, route: string) {
  const { instant } = useMotionPolicy();
  useEffect(() => {
    const root = ref.current;
    if (!root || instant || typeof IntersectionObserver === 'undefined') return;
    const seen = new WeakSet<Element>();
    const observer = new IntersectionObserver(entries => {
      let index = 0;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const node = entry.target as HTMLElement;
        node.style.setProperty('--reveal-delay', `${Math.min(index++ * 45, 135)}ms`);
        node.classList.add('public-revealed');
        observer.unobserve(node);
      }
    }, { threshold: 0.08 });
    const register = (node: Element) => {
      if (seen.has(node) || node.classList.contains('public-revealed')) return;
      seen.add(node);
      observer.observe(node);
    };
    root.querySelectorAll(targets).forEach(register);
    const changes = new MutationObserver(records => {
      for (const record of records) for (const node of Array.from(record.addedNodes)) {
        if (!(node instanceof Element)) continue;
        if (node.matches(targets)) register(node);
        node.querySelectorAll(targets).forEach(register);
      }
    });
    changes.observe(root, { childList: true, subtree: true });
    return () => { observer.disconnect(); changes.disconnect(); };
  }, [ref, route, instant]);
}
